import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { OptionsValues } from '@smartera/core';
import {
	ConditionalFormattingFontSizeOptions,
	ConditionalFormattingFontStyleOptions,
} from '@feature/smart-reports-core/constants';
import { UtilityService } from '@core/services';
import { FormArray } from '@angular/forms';
import { BaseComponent } from '@modules/components';
import { Key } from '@feature/smart-reports-core/models';
import { ElasticSearchService } from '@feature/smart-reports-core/services';
import { map, takeUntil } from 'rxjs/operators';
import { sortList } from '@core/utils';

//TODO: AggregationField için de bu component'in kullanılması ayarlanmalı.

@Component({
	selector: 'field-conditional-formatting',
	templateUrl: './field-conditional-formatting.component.html',
	styleUrls: ['./field-conditional-formatting.component.scss'],
	standalone: false,
})
export class FieldConditionalFormattingComponent extends BaseComponent implements OnChanges {
	readonly FieldConditionalFormattingMethod = FieldConditionalFormattingMethod;
	readonly fontStyleOptions: OptionsValues = ConditionalFormattingFontStyleOptions;
	readonly fontSizeOptions: OptionsValues = ConditionalFormattingFontSizeOptions;

	private readonly defaultFormat: FieldConditionalFormatting = {
		method: FieldConditionalFormattingMethod.EQUALS,
		firstValue: null,
		lastValue: null,
		fontStyle: 'Poppins',
		fontSize: '13px',
		fontColor: '#495057',
	};
	methodOptions: OptionsValues = [];

	@Input() source: string;
	@Input() field: Key;
	@Input() conditions: FieldConditionalFormatting[] = [];
	@Output() conditionsChangeEvent = new EventEmitter<FieldConditionalFormatting[]>();

	fieldDataOptions: OptionsValues = [];

	constructor(
		private elasticSearchService: ElasticSearchService,
		utilityService: UtilityService
	) {
		super(null, utilityService);
		this.form = this.builder.group({
			items: this.builder.array([this.builder.group(this.defaultFormat)]),
		});
	}

	ngOnChanges(): void {
		this.prepareMethodOptions();
		if (this.conditions) {
			this.initializeForm();
		}
	}

	private prepareMethodOptions(): void {
		if (this.field.type === 'text') {
			this.methodOptions = [
				{
					label: this.t(FieldConditionalFormattingMethod.EQUALS.toLowerCase()),
					value: FieldConditionalFormattingMethod.EQUALS,
				},
			];
			this.prepareFieldDataOptions();
		} else {
			this.methodOptions = [
				{
					label: this.t(FieldConditionalFormattingMethod.EQUALS.toLowerCase()),
					value: FieldConditionalFormattingMethod.EQUALS,
				},
				{
					label: this.t(FieldConditionalFormattingMethod.RANGE.toLowerCase()),
					value: FieldConditionalFormattingMethod.RANGE,
				},
			];
		}
	}

	private initializeForm(): void {
		const formItems = this.conditions.map((condition) =>
			this.builder.group({
				method: [condition.method],
				firstValue: [condition.firstValue],
				lastValue: [condition.lastValue],
				fontStyle: [condition.fontStyle],
				fontSize: [condition.fontSize],
				fontColor: [condition.fontColor],
			})
		);
		this.form.setControl('items', this.builder.array(formItems));
	}

	get items(): FormArray {
		return this.form.get('items') as FormArray;
	}

	addItem(): void {
		const items: FormArray = this.form.get('items') as FormArray;
		items.push(this.builder.group(this.defaultFormat));
	}

	removeItem(index: number): void {
		const items = this.form.get('items') as FormArray;
		items.removeAt(index);
	}

	clearFormatting(): void {
		const items = this.form.get('items') as FormArray;
		while (items.length > 1) {
			items.removeAt(0);
		}
		items.at(0).reset(this.defaultFormat);
	}

	save(): void {
		if (this.form) {
			const items = this.form.get('items') as FormArray;
			const updatedConditions: FieldConditionalFormatting[] = items.value;
			this.conditionsChangeEvent.emit(updatedConditions);
			this.hideDialog();
		}
	}

	private prepareFieldDataOptions(): void {
		this.elasticSearchService
			.getFieldsInfo(this.source, [this.field.name])
			.pipe(
				map((fieldInfo) => {
					return sortList(fieldInfo[this.field.name + '_data'].buckets, 'key', 'asc').map((item) => ({
						label: item['key'],
						value: item['key'],
					}));
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((options) => {
				this.fieldDataOptions = options;
			});
	}
}

//TODO: font weight eklenmesi gerekecek
export interface FieldConditionalFormatting {
	method: FieldConditionalFormattingMethod;
	firstValue: string | string[] | Date | number;
	lastValue: string | string[] | Date | number;
	fontStyle: string;
	fontSize: string;
	fontColor: string;
}

export enum FieldConditionalFormattingMethod {
	EQUALS = 'EQUALS',
	RANGE = 'RANGE',
}
